import React, { useEffect, useRef, useState } from 'react';
import Moveable from 'react-moveable';
import useAccessibilityClick from '../../../hooks/useAccessibilityClick';
import '../../../css/Tools.scss';
import { register } from '../../../i18n';
import utilsService from '../../services/UtilsService';

const t2 = register('AriaLabels');

function Protractor() {
  const [target, setTarget] = useState();
  const [frame, setFrame] = useState({
    translate: [0, 0],
    rotate: 0,
    transformOrigin: '50% 50%',
  });

  const moveableRef = useRef();
  const pivotRef = useRef();
  const containerRef = useRef();
  const rotateHandleRef = useRef();

  const handleRotate = () => {
    let snapTo = 0;
    const { rotate } = frame;

    if (rotate === 0) {
      snapTo = 90;
    } else if (rotate === 90) {
      snapTo = 180;
    } else if (rotate === 180) {
      snapTo = 270;
    } else if (rotate === 270) {
      snapTo = 0;
    } else {
      snapTo = Math.abs(rotate);
      snapTo = Math.round(rotate / 90) * 90;
    }

    setFrame(prevFrame => ({
      ...prevFrame,
      rotate: snapTo
    }));

    if (moveableRef.current) {
      moveableRef.current.moveable.request('rotatable', { rotate: snapTo }, true);
    }
  };

  const handleIncrementalRotate = (direction = 1) => {
    if (!moveableRef.current) return;

    const ROTATION_STEP = 1; // 1 degree per activation
    let newRotate = frame.rotate + (ROTATION_STEP * direction);

    // Normalize rotation to 0-360 range
    newRotate = utilsService.subtractFullCircleRotations(newRotate);

    frame.rotate = newRotate;

    moveableRef.current.moveable.request('rotatable', { rotate: newRotate }, true);

    // Keep focus on the rotation handle after rotation
    setTimeout(() => {
      if (rotateHandleRef.current && document.activeElement !== rotateHandleRef.current) {
        rotateHandleRef.current.focus();
      }
    }, 0);
  };

  useAccessibilityClick(pivotRef, handleRotate);

  // CF-4140: Added to fix bug that continued drag even after mouse click.
  const stopDrag = (_event) => {
    moveableRef.current.moveable.stopDrag();
  };

  // Handle keyboard navigation for moving the protractor
  const handleKeyDown = (event) => {
    if (!moveableRef.current) return;

    const STEP_SIZE = 10; // pixels to move per arrow key press
    const SHIFT_STEP_SIZE = 50; // larger step when shift is held
    
    let deltaX = 0;
    let deltaY = 0;
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        deltaX = -STEP_SIZE;
        if (event.shiftKey) deltaX = -SHIFT_STEP_SIZE;
        break;
      case 'ArrowRight':
        event.preventDefault();
        deltaX = STEP_SIZE;
        if (event.shiftKey) deltaX = SHIFT_STEP_SIZE;
        break;
      case 'ArrowUp':
        event.preventDefault();
        deltaY = -STEP_SIZE;
        if (event.shiftKey) deltaY = -SHIFT_STEP_SIZE;
        break;
      case 'ArrowDown':
        event.preventDefault();
        deltaY = STEP_SIZE;
        if (event.shiftKey) deltaY = SHIFT_STEP_SIZE;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleRotate();
        return;
      default:
        return;
    }

    // Update the frame position
    const newTranslate = [
      frame.translate[0] + deltaX,
      frame.translate[1] + deltaY
    ];

    frame.translate = newTranslate;

    // Update the moveable component
    moveableRef.current.moveable.request('draggable', {
      translate: newTranslate
    }, true);
  };

  // Handle click to focus the container
  const handleContainerClick = () => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
  };

  useEffect(() => {
    setTarget(document.querySelector('.protractorTarget'));
    document.addEventListener('mouseup', stopDrag);
    
    // Auto-focus the container when component mounts
    if (containerRef.current) {
      containerRef.current.focus();
    }
    
    return () => {
      document.removeEventListener('mouseup', stopDrag);
    };
  }, []);

  // Separate useEffect for keyboard events to avoid dependency issues
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      container.addEventListener('click', handleContainerClick);

      return () => {
        container.removeEventListener('keydown', handleKeyDown);
        container.removeEventListener('click', handleContainerClick);
      };
    }
  }, [frame.translate]);

  // Add keyboard event listener for rotation handle incremental rotation
  useEffect(() => {
    const handleRotateKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        // Shift+Enter rotates backwards (-1), Enter rotates forwards (1)
        const direction = e.shiftKey ? -1 : 1;
        handleIncrementalRotate(direction);
      }
    };

    const rotateHandle = rotateHandleRef.current;
    if (rotateHandle) {
      rotateHandle.addEventListener('keydown', handleRotateKeyDown);
    }

    return () => {
      if (rotateHandle) {
        rotateHandle.removeEventListener('keydown', handleRotateKeyDown);
      }
    };
  }, []);

  return (
    <div 
      
      className='protractorContainer'
      tabIndex={-1}
    >
      <div
        ref={containerRef}
        aria-label={t2('protractorTool')}
        className='protractorTarget'
        id='protractorTool'
        role='application'
        tabIndex={0}>
        <button
          ref={pivotRef}
          aria-label={t2('rotateProtractor')}
          id='protractorToolRotate90Button'
          tabIndex={0}
        />
        <div id='protractorToolRotator'>
          <div
            ref={rotateHandleRef}
            aria-label={t2('rotateProtractorHandle')}
            id='protractorToolRotateHandle'
            role='button'
            tabIndex={0}
          />
        </div>
      </div>
      <Moveable
        tabIndex={0}
        ref={moveableRef}
        draggable={true}
        onDrag={(e) => {
          frame.translate = e.beforeTranslate;
        }}
        onDragOrigin={(e) => {
          frame.translate = e.drag.beforeTranslate;
          frame.transformOrigin = e.transformOrigin;
        }}
        onDragOriginStart={(e) => {
          e.dragStart && e.dragStart.set(frame.translate);
        }}
        onDragStart={(e) => {
          e.set(frame.translate);
        }}
        onRender={(e) => {
          const { translate, rotate, transformOrigin } = frame;
          e.target.style.transformOrigin = transformOrigin;
          e.target.style.transform = `translate(${translate[0]}px, ${translate[1]}px)`
                    + ` rotate(${rotate}deg)`;
        }}
        onRotate={(e) => {
          // Normalize rotation to 0-360 range
          frame.rotate = utilsService.subtractFullCircleRotations(e.beforeRotate);
        }}
        onRotateStart={(e) => {
          e.set(frame.rotate);
        }}
        origin={false}
        originDraggable={true}
        originRelative={true}
        padding={{ left: 0, top: 0, right: 0, bottom: 0 }}
        rotatable={true}
        rotationPosition='left'
        startDragRotate={0}
        target={target}
        throttleDrag={0}
        throttleDragRotate={0}
        throttleRotate={0}
        zoom={1}
      />
    </div>
  );
}

export default Protractor;
